import { useEffect, useState } from "react";
import { verifyAccessCode } from "../api";

type GateState = "bootstrapping" | "locked" | "submitting" | "unlocked";

interface UseAccessGateArgs {
  accessCode: string | null;
  setAccessCode: (code: string | null) => void;
}

export function useAccessGate({
  accessCode,
  setAccessCode,
}: UseAccessGateArgs) {
  const [codeToVerify, setCodeToVerify] = useState<string | null>(accessCode);
  const [gateState, setGateState] = useState<GateState>(() =>
    accessCode ? "bootstrapping" : "locked",
  );
  const [gateError, setGateError] = useState<string | undefined>(undefined);

  useEffect(() => {
    let cancelled = false; // prevents async requests from finishing if gate is unmounted halfway

    async function run() {
      if (!codeToVerify) return;

      setGateError(undefined);

      try {
        const canView = await verifyAccessCode(codeToVerify);
        if (cancelled) return;

        if (canView) {
          setGateState("unlocked");
          setAccessCode(codeToVerify);
        } else {
          setAccessCode(null);
          setGateError("That access code was not accepted.");
          setGateState("locked");
        }
      } catch {
        if (cancelled) return;
        setAccessCode(null);
        setGateError("Could not verify access code. Please try again.");
        setGateState("locked");
      } finally {
        if (!cancelled) {
          setCodeToVerify(null);
        }
      }
    }

    // start the async function and continue executing
    // needed since react expects useEffects to return a cleanup function, not a promise
    void run();

    return () => {
      cancelled = true;
    };
  }, [codeToVerify, setAccessCode]);

  const submitCode = (code: string) => {
    setGateError(undefined);
    setGateState("submitting");
    setCodeToVerify(code);
  };

  return { gateState, gateError, submitCode };
}
